import { useGetConnectCarrierTenderCarrierPortalCountQuery } from '@connect-carrier/generated/queries/getConnectCarrierTenderCarrierPortalCount';
import { getSelectedCustomer } from '@connect-carrier/harness/customer';
import { useFlagMe405114EnableCarrierPortalMultipleCarrierImplementation } from '@generated/flags/ME-405114-enable-carrier-portal-multiple-carrier-implementation';
import { useEffect, useState } from 'react';

interface UseTendersCountOptions {
  refreshIntervalSeconds?: number;
  enabled?: boolean;
}

interface UseTendersCountReturn {
  count: number;
  loading: boolean;
  error: boolean;
  refetch: () => Promise<anyOk>;
}

/**
 * Hook to fetch and auto-refresh tenders count for the portal carrier
 * @param options Configuration options including refresh interval
 * @returns Object containing count, loading state, and error state
 */
// ts-unused-exports:disable-next-line
export const useTendersCount = (
  options: UseTendersCountOptions = {},
): UseTendersCountReturn => {
  const {
    refreshIntervalSeconds = 30, // Default to 30 seconds
    enabled = true,
  } = options;

  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);

  // Get feature flag for multiple carrier implementation
  const enableMultipleCarrier =
    useFlagMe405114EnableCarrierPortalMultipleCarrierImplementation();

  // Get selected customer to extract carrierId(s)
  const selectedCustomer = getSelectedCustomer();

  // Determine carrierId based on feature flag and customer data
  const carrierId = ((): string | string[] | null => {
    if (
      !selectedCustomer ||
      (!selectedCustomer.mmRecordId &&
        !selectedCustomer.mmApprovedRecordIds?.length)
    ) {
      return null;
    }

    return enableMultipleCarrier
      ? ((selectedCustomer.mmApprovedRecordIds as string[]) ?? null)
      : (selectedCustomer.mmRecordId ?? null);
  })();

  // Check if we have valid carrierId(s) to query with
  const hasValidCarrierId =
    carrierId &&
    ((typeof carrierId === 'string' && carrierId.length > 0) ||
      (Array.isArray(carrierId) && carrierId.length > 0));

  // Build filter object for tender count
  const filter: anyOk = {};

  if (hasValidCarrierId) {
    filter.carrierId = carrierId;
  }

  // Query for tenders count with carrierId filter to get total available tenders for the carrier
  const {
    data,
    loading,
    error: queryError,
    refetch,
    startPolling,
    stopPolling,
  } = useGetConnectCarrierTenderCarrierPortalCountQuery({
    variables: {
      filter,
    },
    fetchPolicy: 'no-cache',
    skip: !enabled || !hasValidCarrierId, // Skip if disabled or no valid carrierId available
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    pollInterval: refreshIntervalSeconds * 1000, // Polling for auto-refresh
  });

  // Update count when data changes
  useEffect(() => {
    if (data?.getConnectCarrierTenderCarrierPortalCount?.count !== undefined) {
      setCount(data.getConnectCarrierTenderCarrierPortalCount.count);
      setError(false);
    } else if (queryError) {
      setError(true);
    }
  }, [data, queryError]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!enabled || refreshIntervalSeconds <= 0 || !hasValidCarrierId) {
      return;
    }
    startPolling(refreshIntervalSeconds * 1000);

    return () => {
      stopPolling();
    };
  }, [enabled, refreshIntervalSeconds, refetch, hasValidCarrierId]);

  return {
    count: error ? 0 : count,
    loading,
    error,
    refetch,
  };
};
